import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import { useEffect, useRef, useState } from "react";

import type {
  Tournament,
  TournamentComplianceRequirementOverrides,
  TournamentCreate,
} from "@/libs/tournamentApi";
import {
  normalizePricingModel,
  normalizeTournamentType,
} from "@/libs/tournamentTypes";

interface Props {
  open: boolean;
  tournament?: Tournament | null;
  mode?: "create" | "edit" | "clone";
  title: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: (data: TournamentFormData) => Promise<void>;
}

const toDateInput = (iso: string | null | undefined) =>
  iso ? iso.slice(0, 10) : "";
const toIso = (date: string) => (date ? `${date}T00:00:00Z` : null);

export type TournamentFormData = Omit<
  TournamentCreate,
  "is_published" | "compliance_requirements"
> & {
  compliance_requirements?: TournamentComplianceRequirementOverrides | null;
};

type ComplianceRequirementsForm = {
  [Key in keyof NonNullable<TournamentComplianceRequirementOverrides>]: boolean;
};

const ORDINARY_LOCAL_REQUIREMENTS: ComplianceRequirementsForm = {
  require_federation_membership: false,
  require_ncsi_for_18plus: false,
  require_foreign_country_declaration: false,
};

type TournamentFormState = TournamentFormData & {
  displayed_compliance_requirements: ComplianceRequirementsForm;
};

const EMPTY_FORM: TournamentFormState = {
  name: "",
  description: "",
  tournament_type: "individual",
  pricing_model: "per_event",
  start_date: null,
  end_date: null,
  registration_open_at: null,
  registration_close_at: null,
  compliance_requirements: ORDINARY_LOCAL_REQUIREMENTS,
  displayed_compliance_requirements: ORDINARY_LOCAL_REQUIREMENTS,
};

export function TournamentFormDialog({
  open,
  tournament,
  mode,
  title,
  submitLabel,
  onClose,
  onSubmit,
}: Props) {
  const formMode = mode ?? (tournament ? "edit" : "create");
  const [form, setForm] = useState<TournamentFormState>({ ...EMPTY_FORM });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    dates?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const mountedRef = useRef(true);
  const sessionGenerationRef = useRef(0);
  const inFlightSessionRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      inFlightSessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    inFlightSessionRef.current = null;
    setSaving(false);
    if (!open) return;

    setError(null);
    setFieldErrors({});
    setForm(
      tournament
        ? {
            name: tournament.name,
            description: tournament.description ?? "",
            tournament_type: normalizeTournamentType(
              tournament.tournament_type,
            ),
            pricing_model: normalizePricingModel(tournament.pricing_model),
            start_date: tournament.start_date,
            end_date: tournament.end_date,
            registration_open_at: tournament.registration_open_at,
            registration_close_at: tournament.registration_close_at,
            compliance_requirements:
              formMode === "clone" && tournament.compliance_requirements
                ? {
                    ...tournament.compliance_requirements,
                  }
                : undefined,
            displayed_compliance_requirements: {
              ...tournament.compliance_requirements,
            },
          }
        : { ...EMPTY_FORM },
    );
  }, [formMode, open, tournament?.tournament_id]);

  const set = <Key extends keyof TournamentFormState>(
    key: Key,
    value: TournamentFormState[Key],
  ) => setForm((previous) => ({ ...previous, [key]: value }));

  const setComplianceRequirement = (
    key: keyof ComplianceRequirementsForm,
    value: boolean,
  ) => {
    setForm((previous) => ({
      ...previous,
      compliance_requirements: {
        ...previous.compliance_requirements,
        [key]: value,
      },
      displayed_compliance_requirements: {
        ...previous.displayed_compliance_requirements,
        [key]: value,
      },
    }));
  };

  const requestClose = () => {
    if (inFlightSessionRef.current !== null) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (inFlightSessionRef.current !== null) return;

    const nextFieldErrors: typeof fieldErrors = {};
    if (!form.name.trim()) {
      nextFieldErrors.name = "Name is required.";
    }

    const startTime = form.start_date ? Date.parse(form.start_date) : null;
    const endTime = form.end_date ? Date.parse(form.end_date) : null;
    if (
      startTime !== null &&
      endTime !== null &&
      Number.isFinite(startTime) &&
      Number.isFinite(endTime) &&
      startTime > endTime
    ) {
      nextFieldErrors.dates = "Start date must be on or before end date.";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Fix the highlighted fields before saving.");
      return;
    }

    const sessionGeneration = sessionGenerationRef.current;
    inFlightSessionRef.current = sessionGeneration;
    setFieldErrors({});
    setError(null);
    setSaving(true);
    try {
      const {
        displayed_compliance_requirements: _displayed,
        compliance_requirements,
        ...rest
      } = form;
      const payload =
        compliance_requirements === undefined
          ? rest
          : { ...rest, compliance_requirements };
      await onSubmit(payload);
      if (
        !mountedRef.current ||
        sessionGenerationRef.current !== sessionGeneration
      ) {
        return;
      }
      onClose();
    } catch (cause) {
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setError(cause instanceof Error ? cause.message : "Save failed");
      }
    } finally {
      if (inFlightSessionRef.current === sessionGeneration) {
        inFlightSessionRef.current = null;
      }
      if (
        mountedRef.current &&
        sessionGenerationRef.current === sessionGeneration
      ) {
        setSaving(false);
      }
    }
  };

  const dateFieldProps = {
    fullWidth: true,
    size: "small" as const,
    slotProps: { inputLabel: { shrink: true } },
    type: "date",
  };

  return (
    <Dialog
      aria-describedby="tournament-form-description"
      aria-labelledby="tournament-form-title"
      fullWidth
      maxWidth="sm"
      onClose={requestClose}
      open={open}
    >
      <DialogTitle id="tournament-form-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText id="tournament-form-description">
            Set the tournament name, format, schedule, registration window, and
            description.
          </DialogContentText>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            autoFocus
            error={Boolean(fieldErrors.name)}
            fullWidth
            helperText={fieldErrors.name}
            id="t-name"
            label="Tournament name"
            onChange={(event) => set("name", event.target.value)}
            size="small"
            value={form.name}
          />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="t-type-label">Type</InputLabel>
              <Select
                label="Type"
                labelId="t-type-label"
                onChange={(event) =>
                  set(
                    "tournament_type",
                    event.target.value as TournamentFormData["tournament_type"],
                  )
                }
                value={form.tournament_type}
              >
                <MenuItem value="individual">Individual</MenuItem>
                <MenuItem value="team">Team</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="t-pricing-label">Pricing</InputLabel>
              <Select
                label="Pricing"
                labelId="t-pricing-label"
                onChange={(event) =>
                  set(
                    "pricing_model",
                    event.target.value as TournamentFormData["pricing_model"],
                  )
                }
                value={form.pricing_model}
              >
                <MenuItem value="per_event">Per event</MenuItem>
                <MenuItem value="tiered_volume">Tiered (volume)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <TextField
              {...dateFieldProps}
              error={Boolean(fieldErrors.dates)}
              id="t-start"
              label="Start date"
              onChange={(event) => set("start_date", toIso(event.target.value))}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  "aria-describedby": fieldErrors.dates
                    ? "t-dates-error"
                    : undefined,
                },
              }}
              value={toDateInput(form.start_date)}
            />
            <TextField
              {...dateFieldProps}
              error={Boolean(fieldErrors.dates)}
              id="t-end"
              label="End date"
              onChange={(event) => set("end_date", toIso(event.target.value))}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  "aria-describedby": fieldErrors.dates
                    ? "t-dates-error"
                    : undefined,
                },
              }}
              value={toDateInput(form.end_date)}
            />
            {fieldErrors.dates ? (
              <FormHelperText
                error
                id="t-dates-error"
                sx={{ gridColumn: "1 / -1", mt: -1 }}
              >
                {fieldErrors.dates}
              </FormHelperText>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <TextField
              {...dateFieldProps}
              id="t-ro"
              label="Registration opens"
              onChange={(event) =>
                set("registration_open_at", toIso(event.target.value))
              }
              value={toDateInput(form.registration_open_at)}
            />
            <TextField
              {...dateFieldProps}
              id="t-rc"
              label="Registration closes"
              onChange={(event) =>
                set("registration_close_at", toIso(event.target.value))
              }
              value={toDateInput(form.registration_close_at)}
            />
          </Box>

          <TextField
            fullWidth
            id="t-desc"
            label="Description"
            minRows={2}
            multiline
            onChange={(event) => set("description", event.target.value)}
            value={form.description ?? ""}
          />

          <FormControl component="fieldset" fullWidth>
            <FormHelperText sx={{ mt: 0 }}>
              Choose the entry requirements that apply to this tournament.
            </FormHelperText>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ flexWrap: "wrap" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.displayed_compliance_requirements
                        .require_federation_membership
                    }
                    onChange={(event) =>
                      setComplianceRequirement(
                        "require_federation_membership",
                        event.target.checked,
                      )
                    }
                  />
                }
                label="Require federation membership"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.displayed_compliance_requirements
                        .require_ncsi_for_18plus
                    }
                    onChange={(event) =>
                      setComplianceRequirement(
                        "require_ncsi_for_18plus",
                        event.target.checked,
                      )
                    }
                  />
                }
                label="Require adult background check"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      form.displayed_compliance_requirements
                        .require_foreign_country_declaration
                    }
                    onChange={(event) =>
                      setComplianceRequirement(
                        "require_foreign_country_declaration",
                        event.target.checked,
                      )
                    }
                  />
                }
                label="Require foreign country declaration"
              />
            </Stack>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={saving}
          onClick={requestClose}
          size="small"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleSubmit}
          size="small"
          variant="contained"
        >
          {saving ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
